import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Loader2,
  CheckCircle,
  AlertCircle,
  MessageSquare,
  ListTodo,
  Calendar,
  FileText,
  Zap,
  X,
  Send
} from "lucide-react";

// Voice command patterns
const COMMAND_PATTERNS = {
  ADD_TASK: /^(add task|new task|create task|task|todo)[\s:]+(.+)/i,
  ADD_MEETING: /^(add meeting|schedule meeting|new meeting|meeting)[\s:]+(.+)/i,
  ADD_NOTE: /^(note|quick note|add note|capture)[\s:]+(.+)/i,
  SWITCH_FACILITY: /^(switch to|go to|change to|open)\s+(.+)/i,
  ASK_AI: /^(ask|hey niko|niko|assistant)[\s:]+(.+)/i,
  WHATS_TODAY: /^(what'?s today|today'?s schedule|my day|what do i have)/i,
  ADD_INCIDENT: /^(incident|log incident|report incident)[\s:]+(.+)/i,
  MORNING_BRIEFING: /^(morning briefing|briefing|brief me|give me my briefing|commute briefing)/i,
};

// Parse natural language for meeting details
const parseMeetingDetails = (text, facilities = []) => {
  const result = {
    title: text,
    date: new Date().toISOString().split('T')[0],
    startTime: 9,
    endTime: 10
  };
  
  // Parse time patterns
  const timeMatch = text.match(/at\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);
  if (timeMatch) {
    let hour = parseInt(timeMatch[1]);
    const minutes = timeMatch[2] ? parseInt(timeMatch[2]) : 0;
    const ampm = timeMatch[3]?.toLowerCase();
    
    if (ampm === 'pm' && hour < 12) hour += 12;
    if (ampm === 'am' && hour === 12) hour = 0;
    
    result.startTime = hour + minutes / 60;
    result.endTime = result.startTime + 1;
    result.title = text.replace(/at\s+\d{1,2}(?::\d{2})?\s*(am|pm)?/i, '').trim();
  }
  
  // Parse date patterns
  const tomorrowMatch = text.match(/tomorrow/i);
  const nextWeekMatch = text.match(/next\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i);
  
  if (tomorrowMatch) {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    result.date = tomorrow.toISOString().split('T')[0];
    result.title = result.title.replace(/tomorrow/i, '').trim();
  }
  
  // Parse facility — match any facility from the caller's list
  const facilityTokens = facilities
    .flatMap(f => [f.name, f.short_name])
    .filter(Boolean)
    .map(t => t.toLowerCase());
  if (facilityTokens.length) {
    const escaped = facilityTokens.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
    const facRe = new RegExp(`(at|for)\\s+(${escaped})`, 'i');
    const facilityMatch = text.match(facRe);
    if (facilityMatch) {
      result.facilityHint = facilityMatch[2].toLowerCase();
      result.title = result.title.replace(facRe, '').trim();
    }
  }

  return result;
};

export default function VoiceAssistant({ 
  facilities = [], 
  onTaskCreated,
  onMeetingCreated,
  onFacilitySwitch,
  onNavigate 
}) {
  const { user } = useAuth();
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [lastAction, setLastAction] = useState(null);
  const [voiceFeedback, setVoiceFeedback] = useState(true);
  const [aiResponse, setAiResponse] = useState("");
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [micError, setMicError] = useState("");
  const [typed, setTyped] = useState("");
  
  const recognitionRef = useRef(null);
  const synthRef = useRef(null);
  const handleVoiceInputRef = useRef(null);

  const speechSupported = typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  const isIOS = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent);

  const MIC_ERRORS = {
    'not-allowed': isIOS
      ? "Microphone blocked. On iPhone: Settings → Safari → Microphone → Allow, then reload."
      : "Microphone access denied. Allow the mic for this site and try again.",
    'service-not-allowed': isIOS
      ? "iPhone dictation is off. Settings → General → Keyboard → turn on Enable Dictation, then try again."
      : "Speech service isn't available in this browser.",
    'audio-capture': "No microphone found. Check your device's mic.",
    'network': "Speech service needs a network connection. Check your signal and retry.",
    'no-speech': "Didn't catch that — tap the mic and speak right away.",
    'aborted': "",
    'language-not-supported': "This browser can't transcribe en-US speech.",
  };
  
  // Speech synthesis only — recognition is created lazily inside the tap (iOS requires the user gesture)
  useEffect(() => {
    if (typeof window !== 'undefined') synthRef.current = window.speechSynthesis;
    return () => { try { recognitionRef.current?.abort(); } catch { /* noop */ } };
  }, []);

  const buildRecognition = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return null;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.onstart = () => { setIsListening(true); setMicError(""); };
    recognition.onresult = (event) => {
      let interim = "", final = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) final += t; else interim += t;
      }
      if (final) {
        setTranscript(final);
        setInterimTranscript("");
        handleVoiceInputRef.current?.(final);
      } else {
        setInterimTranscript(interim);
      }
    };
    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
      setIsListening(false);
      const msg = MIC_ERRORS[event.error] ?? `Speech error: ${event.error}. You can type your command below.`;
      if (msg) setMicError(msg);
    };
    recognition.onend = () => setIsListening(false);
    return recognition;
  };
  
  // Speak text aloud
  const speak = useCallback((text) => {
    if (!voiceFeedback || !synthRef.current) return;
    
    // Cancel any ongoing speech
    synthRef.current.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 0.8;
    
    // Try to use a natural voice
    const voices = synthRef.current.getVoices();
    const preferredVoice = voices.find(v => 
      v.name.includes('Samantha') || 
      v.name.includes('Google') || 
      v.name.includes('Natural')
    );
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }
    
    synthRef.current.speak(utterance);
  }, [voiceFeedback]);
  
  // Toggle listening
  const toggleListening = () => {
    setShowDialog(true);
    if (isListening) {
      try { recognitionRef.current?.stop(); } catch { /* noop */ }
      setIsListening(false);
      return;
    }
    setTranscript("");
    setInterimTranscript("");
    setAiResponse("");
    setLastAction(null);
    setMicError("");

    if (!speechSupported) {
      setMicError(isIOS
        ? "Voice input isn't available in this browser. Use Safari (not the Chrome app) or type your command below."
        : "Speech recognition isn't supported in this browser. Type your command below.");
      return;
    }
    try {
      // iOS Safari: a fresh recognizer per tap, created inside the gesture, is the reliable path
      try { recognitionRef.current?.abort(); } catch { /* noop */ }
      recognitionRef.current = buildRecognition();
      recognitionRef.current.start();
      setIsListening(true);
    } catch (error) {
      console.error("Failed to start recognition:", error);
      setIsListening(false);
      setMicError("Couldn't start the microphone. Type your command below instead.");
    }
  };

  const submitTyped = (e) => {
    e?.preventDefault?.();
    const text = typed.trim();
    if (!text || isProcessing || isAiThinking) return;
    try { recognitionRef.current?.abort(); } catch { /* noop */ }
    setIsListening(false);
    setTyped("");
    setMicError("");
    setLastAction(null);
    setAiResponse("");
    setTranscript(text);
    handleVoiceInput(text);
  };
  
  // Process voice input and determine action
  const handleVoiceInput = async (text) => {
    setIsProcessing(true);
    const lowerText = text.toLowerCase().trim();

    try {
      // -------- 1. Try smart LLM intent parsing first ------------------ //
      let intent = null;
      let params = {};
      let smartReply = "";
      try {
        const { data } = await axios.post(
          `${API}/voice/parse`,
          { transcript: text },
          { withCredentials: true }
        );
        intent = data?.intent;
        params = data?.params || {};
        smartReply = data?.reply || "";
      } catch (err) {
        console.warn("voice/parse failed, falling back to regex", err);
      }

      if (intent === "add_meeting") {
        const details = {
          title: params.title || text,
          date: params.date || new Date().toISOString().split("T")[0],
          startTime: typeof params.start_time === "number" ? params.start_time : 9,
          endTime: typeof params.end_time === "number" ? params.end_time :
            (typeof params.start_time === "number" ? params.start_time + 1 : 10),
          facilityHint: params.facility_hint || null,
        };
        await createMeeting(details, smartReply);
        return;
      }
      if (intent === "add_task") {
        await createTask(params.description || text, params.priority || "Today", smartReply);
        return;
      }
      if (intent === "add_note") {
        await createNote(params.text || text, smartReply);
        return;
      }
      if (intent === "switch_facility") {
        switchFacility(params.facility || "");
        return;
      }
      if (intent === "todays_plan") {
        await getTodaysSummary();
        return;
      }
      if (intent === "morning_briefing") {
        await getMorningBriefing();
        return;
      }
      if (intent === "ask_ai") {
        await askAI(params.question || text);
        return;
      }

      // -------- 2. Regex fallback (offline / LLM error) ---------------- //

      // ADD TASK
      const taskMatch = text.match(COMMAND_PATTERNS.ADD_TASK);
      if (taskMatch) {
        await createTask(taskMatch[2].trim());
        return;
      }

      // ADD MEETING
      const meetingMatch = text.match(COMMAND_PATTERNS.ADD_MEETING);
      if (meetingMatch) {
        await createMeeting(parseMeetingDetails(meetingMatch[2], facilities));
        return;
      }

      // ADD NOTE
      const noteMatch = text.match(COMMAND_PATTERNS.ADD_NOTE);
      if (noteMatch) {
        await createNote(noteMatch[2].trim());
        return;
      }

      // SWITCH FACILITY
      const switchMatch = text.match(COMMAND_PATTERNS.SWITCH_FACILITY);
      if (switchMatch) {
        switchFacility(switchMatch[2].toLowerCase());
        return;
      }

      // WHAT'S TODAY
      if (COMMAND_PATTERNS.WHATS_TODAY.test(lowerText)) {
        await getTodaysSummary();
        return;
      }

      // MORNING BRIEFING
      if (COMMAND_PATTERNS.MORNING_BRIEFING.test(lowerText)) {
        await getMorningBriefing();
        return;
      }

      // Default: Send to AI
      await askAI(text);

    } catch (error) {
      console.error("Error processing voice input:", error);
      setLastAction({ type: 'error', message: 'Something went wrong. Please try again.' });
      speak("Sorry, something went wrong. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Create a task
  const createTask = async (description, priority = "Today", spokenReply = "") => {
    try {
      const response = await axios.post(`${API}/tasks`, {
        description,
        priority,
        category: "Quick Capture"
      }, { withCredentials: true });

      setLastAction({
        type: 'task',
        message: `Task added: "${description}"`,
        data: response.data
      });
      speak(spokenReply || `Got it. Task added: ${description}`);
      onTaskCreated?.(response.data);
      toast.success("Task created!");
    } catch (error) {
      throw error;
    }
  };

  // Create a meeting
  const createMeeting = async (details, spokenReply = "") => {
    try {
      // Find facility if mentioned
      let facilityId = null;
      if (details.facilityHint) {
        const facility = facilities.find(f =>
          f.name.toLowerCase().includes(details.facilityHint) ||
          f.short_name?.toLowerCase().includes(details.facilityHint)
        );
        if (facility) facilityId = facility.facility_id;
      }

      const response = await axios.post(`${API}/meetings`, {
        title: details.title,
        date: details.date,
        start_time: details.startTime,
        end_time: details.endTime,
        facility_id: facilityId,
        category: "work"
      }, { withCredentials: true });

      const timeStr = formatTime(details.startTime);
      setLastAction({
        type: 'meeting',
        message: `Meeting scheduled: "${details.title}" on ${details.date} at ${timeStr}`,
        data: response.data
      });
      speak(spokenReply || `Meeting scheduled: ${details.title} at ${timeStr}`);
      onMeetingCreated?.(response.data);
      toast.success("Meeting created!");
    } catch (error) {
      throw error;
    }
  };

  // Create a quick note
  const createNote = async (text, spokenReply = "") => {
    try {
      // Store as a task with "Note" category for now
      const response = await axios.post(`${API}/tasks`, {
        description: `📝 ${text}`,
        priority: "Low",
        category: "Notes"
      }, { withCredentials: true });

      setLastAction({
        type: 'note',
        message: `Note captured: "${text}"`,
        data: response.data
      });
      speak(spokenReply || `Note captured: ${text}`);
      toast.success("Note saved!");
    } catch (error) {
      throw error;
    }
  };
  
  // Switch facility context
  const switchFacility = (facilityName) => {
    const facility = facilities.find(f => 
      f.name.toLowerCase().includes(facilityName) ||
      f.short_name?.toLowerCase().includes(facilityName)
    );
    
    if (facility) {
      setLastAction({ 
        type: 'switch', 
        message: `Switched to ${facility.name}`,
        data: facility
      });
      speak(`Switching to ${facility.name}`);
      onFacilitySwitch?.(facility);
      toast.success(`Switched to ${facility.name}`);
    } else if (facilityName === 'personal') {
      setLastAction({ 
        type: 'switch', 
        message: 'Switched to Personal mode'
      });
      speak("Switching to Personal mode");
      onFacilitySwitch?.({ name: 'Personal', facility_id: 'personal' });
    } else {
      speak(`I couldn't find a facility called ${facilityName}`);
    }
  };
  
  // Get today's summary
  const getTodaysSummary = async () => {
    setIsAiThinking(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const [meetingsRes, tasksRes, aocRes] = await Promise.all([
        axios.get(`${API}/meetings?date=${today}`, { withCredentials: true }),
        axios.get(`${API}/tasks`, { withCredentials: true }),
        axios.get(`${API}/aoc/today`, { withCredentials: true })
      ]);
      
      const meetings = (meetingsRes.data || []).sort((a, b) => (a.start_time || 0) - (b.start_time || 0));
      const tasks = (tasksRes.data || []).filter(t => !t.completed);
      const aoc = aocRes.data;

      let summary = "";

      if (aoc?.is_on_call) {
        summary += `You're on call today for ${aoc.shifts.map(s => s.facility_name).join(' and ')}. `;
      }

      if (meetings.length === 0) {
        summary += `You have no meetings on the calendar today. `;
      } else {
        summary += `You have ${meetings.length} meeting${meetings.length === 1 ? '' : 's'} today. `;
        const first = meetings[0];
        summary += `First up: "${first.title}" at ${formatTime(first.start_time)}. `;
        if (meetings.length > 1) {
          const rest = meetings.slice(1, 3).map(m => `"${m.title}" at ${formatTime(m.start_time)}`).join(', then ');
          summary += `Then ${rest}. `;
        }
      }

      summary += `${tasks.length} task${tasks.length === 1 ? '' : 's'} still open.`;
      
      setAiResponse(summary);
      setLastAction({ 
        type: 'summary', 
        message: summary
      });
      speak(summary);
    } catch (error) {
      console.error("Error getting summary:", error);
      speak("I couldn't get your schedule right now.");
    } finally {
      setIsAiThinking(false);
    }
  };
  
  // Get full morning briefing with weather and news
  const getMorningBriefing = async () => {
    setIsAiThinking(true);
    try {
      // Get location from localStorage or use auto
      const savedLocation = localStorage.getItem('niko_weather_location') || 'auto';
      const response = await axios.get(`${API}/briefing/morning?location=${encodeURIComponent(savedLocation)}`, { 
        withCredentials: true 
      });
      
      const briefing = response.data;
      const summaryText = briefing.summary_text || "I couldn't generate your briefing right now.";
      
      setAiResponse(summaryText);
      setLastAction({ 
        type: 'summary', 
        message: summaryText
      });
      speak(summaryText);
    } catch (error) {
      console.error("Error getting briefing:", error);
      speak("I couldn't get your morning briefing right now. Please try again.");
    } finally {
      setIsAiThinking(false);
    }
  };
  
  // Ask AI assistant
  const askAI = async (question) => {
    setIsAiThinking(true);
    try {
      const response = await axios.post(`${API}/ai/chat`, {
        message: question,
        context: "voice_assistant"
      }, { withCredentials: true });
      
      const aiText = response.data?.response || response.data?.message || "I'm not sure how to help with that.";
      
      // Clean up markdown for speech
      const cleanText = aiText.replace(/\*\*/g, '').replace(/\*/g, '').replace(/#{1,6}\s/g, '');
      
      setAiResponse(cleanText);
      setLastAction({ 
        type: 'ai', 
        message: cleanText
      });
      
      // Speak a truncated version if too long
      const speakText = cleanText.length > 300 ? cleanText.substring(0, 300) + "... I've shown the full response on screen." : cleanText;
      speak(speakText);
    } catch (error) {
      console.error("Error asking AI:", error);
      const errorMsg = "I couldn't process that request right now. Please try again.";
      setAiResponse(errorMsg);
      speak(errorMsg);
    } finally {
      setIsAiThinking(false);
    }
  };
  
  // Format time helper
  const formatTime = (hour) => {
    const h = Math.floor(hour);
    const m = Math.round((hour - h) * 60);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayH = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${displayH}:${m.toString().padStart(2, '0')} ${ampm}`;
  };
  
  // Get action icon
  handleVoiceInputRef.current = handleVoiceInput;
  const getActionIcon = (type) => {
    switch (type) {
      case 'task': return <ListTodo className="w-5 h-5 text-green-400" />;
      case 'meeting': return <Calendar className="w-5 h-5 text-blue-400" />;
      case 'note': return <FileText className="w-5 h-5 text-yellow-400" />;
      case 'switch': return <Zap className="w-5 h-5 text-purple-400" />;
      case 'summary': return <MessageSquare className="w-5 h-5 text-cyan-400" />;
      case 'ai': return <MessageSquare className="w-5 h-5 text-primary" />;
      case 'error': return <AlertCircle className="w-5 h-5 text-red-400" />;
      default: return <CheckCircle className="w-5 h-5 text-green-400" />;
    }
  };

  return (
    <>
      {/* Floating Voice Button — bumped up on phone so it clears the
          BottomTabBar (md+ keeps the original bottom-6 position). */}
      <div className="fixed bottom-20 md:bottom-6 right-6 z-40">
        <Button
          onClick={toggleListening}
          size="lg"
          className={`rounded-full w-14 h-14 shadow-lg transition-all ${
            isListening 
              ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
              : 'bg-primary hover:bg-primary/90'
          }`}
          data-testid="voice-assistant-btn"
        >
          {isListening ? (
            <MicOff className="w-6 h-6" />
          ) : (
            <Mic className="w-6 h-6" />
          )}
        </Button>
        
        {/* Quick hint — tappable too, so tapping the label works like tapping the mic */}
        {!showDialog && (
          <button
            type="button"
            onClick={toggleListening}
            className="absolute -top-8 right-0 text-xs text-muted-foreground whitespace-nowrap"
            data-testid="voice-assistant-hint"
          >
            Tap to speak
          </button>
        )}
      </div>
      
      {/* Voice Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Mic className="w-5 h-5 text-primary" />
                Niko Voice Assistant
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setVoiceFeedback(!voiceFeedback)}
                className="h-8 w-8 p-0"
              >
                {voiceFeedback ? (
                  <Volume2 className="w-4 h-4" />
                ) : (
                  <VolumeX className="w-4 h-4 text-muted-foreground" />
                )}
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Listening indicator */}
            <div className={`p-6 rounded-lg text-center ${isListening ? 'bg-primary/10' : 'bg-zinc-800/50'}`}>
              {isListening ? (
                <div className="space-y-3">
                  <div className="flex justify-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <div
                        key={i}
                        className="w-1 bg-primary rounded-full animate-pulse"
                        style={{
                          height: `${20 + Math.random() * 20}px`,
                          animationDelay: `${i * 0.1}s`
                        }}
                      />
                    ))}
                  </div>
                  <p className="text-sm text-primary font-medium">Listening...</p>
                  {interimTranscript && (
                    <p className="text-sm text-muted-foreground italic">"{interimTranscript}"</p>
                  )}
                </div>
              ) : isProcessing || isAiThinking ? (
                <div className="space-y-3">
                  <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">
                    {isAiThinking ? "Thinking..." : "Processing..."}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <Button
                    onClick={toggleListening}
                    size="lg"
                    className="rounded-full w-16 h-16"
                    disabled={!speechSupported}
                    data-testid="voice-dialog-mic-btn"
                  >
                    <Mic className="w-8 h-8" />
                  </Button>
                  <p className="text-sm text-muted-foreground">{speechSupported ? "Tap to speak" : "Voice unavailable here — type below"}</p>
                </div>
              )}
            </div>

            {/* Mic problem — always visible, with device-specific fix */}
            {micError && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/40 flex items-start gap-2" data-testid="voice-mic-error">
                <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-100">{micError}</p>
              </div>
            )}

            {/* Type-to-command fallback */}
            <form onSubmit={submitTyped} className="flex items-center gap-2" data-testid="voice-typed-form">
              <input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder="Or type a command… e.g. Add task: call pharmacy"
                className="flex-1 h-10 rounded-md border border-zinc-700 bg-zinc-900/60 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={isProcessing || isAiThinking}
                data-testid="voice-typed-input"
              />
              <Button type="submit" size="icon" className="h-10 w-10 shrink-0" disabled={!typed.trim() || isProcessing || isAiThinking} data-testid="voice-typed-submit">
                <Send className="w-4 h-4" />
              </Button>
            </form>
            
            {/* Transcript */}
            {transcript && (
              <div className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <p className="text-xs text-muted-foreground mb-1">You said:</p>
                <p className="text-sm">"{transcript}"</p>
              </div>
            )}
            
            {/* Action result */}
            {lastAction && (
              <div className={`p-3 rounded-lg border ${
                lastAction.type === 'error' 
                  ? 'bg-red-500/10 border-red-500/30' 
                  : 'bg-green-500/10 border-green-500/30'
              }`}>
                <div className="flex items-start gap-2">
                  {getActionIcon(lastAction.type)}
                  <p className="text-sm flex-1">{lastAction.message}</p>
                </div>
              </div>
            )}
            
            {/* AI Response */}
            {aiResponse && lastAction?.type === 'ai' && (
              <div className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700 max-h-48 overflow-y-auto">
                <p className="text-sm whitespace-pre-wrap">{aiResponse}</p>
              </div>
            )}
            
            {/* Command hints */}
            <div className="text-xs text-muted-foreground space-y-1">
              <p className="font-medium">Try saying:</p>
              <ul className="space-y-0.5 ml-2">
                <li>• <strong>"Morning briefing"</strong> - Weather, news, schedule</li>
                <li>• "Add task: Review infection reports"</li>
                <li>• "Schedule meeting tomorrow at 2pm Quality Review"</li>
                <li>• "Switch to Miramont"</li>
                <li>• "What's my schedule today?"</li>
                <li>• "Ask Niko: What QI tool should I use for..."</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
